import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Staff from "@/pages/Staff";
import Templates from "@/pages/Templates";
import FormBuilder from "@/pages/FormBuilder";
import { FooterSettings } from "@/components/FooterSettings";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Files, Globe, MapPin, UsersThree, Buildings, CaretRight, MagnifyingGlass, IdentificationBadge, DownloadSimple, ClockCounterClockwise, Trash, Plus, PencilSimple, ListChecks } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS as ROLE_LABELS_MAP } from "@/lib/constants";
import { toast } from "sonner";

const Count = ({ n }) => (
  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary tabular-nums">{n.toLocaleString()}</span>
);

export default function Organization() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ regions: [], chapters: [], facilities: [] });
  const [facSearch, setFacSearch] = useState("");
  const [managers, setManagers] = useState([]);
  const [mstats, setMstats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [auditExporting, setAuditExporting] = useState(false);
  const isAdmin = ["super_admin", "national_manager"].includes(user.role);
  const isSuper = user.role === "super_admin";
  const [params, setParams] = useSearchParams();
  const sections = isAdmin ? ["regions", "chapters", "facilities", "staff", "managers", "templates", ...(isSuper ? ["forms"] : []), "audit", "footer"] : ["staff"];
  const section = sections.includes(params.get("section")) ? params.get("section") : sections[0];
  const setSection = (value) => setParams({ section: value });
  const [facDialog, setFacDialog] = useState(null); // {name, region, district}
  const [chapDialog, setChapDialog] = useState(null); // {id?, name, region, facilities[]}
  const [chapFacSearch, setChapFacSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [districtOpts, setDistrictOpts] = useState([]);
  const [chapDistrictOpts, setChapDistrictOpts] = useState([]);
  const regionNames = data.regions.map((r) => r.name);

  useEffect(() => {
    if (!facDialog?.region) { setDistrictOpts([]); return; }
    api.get("/facilities-reference/districts", { params: { region: facDialog.region } })
      .then((r) => setDistrictOpts(r.data)).catch(() => setDistrictOpts([]));
  }, [facDialog?.region]);

  useEffect(() => {
    if (!chapDialog?.region) { setChapDistrictOpts([]); return; }
    api.get("/facilities-reference/districts", { params: { region: chapDialog.region } })
      .then((r) => setChapDistrictOpts(r.data)).catch(() => setChapDistrictOpts([]));
  }, [chapDialog?.region]);

  const saveFacility = async () => {
    setSaving(true);
    try {
      if (facDialog.original) {
        await api.post("/orgs/facilities/edit", {
          original_name: facDialog.original, name: facDialog.name.trim(), region: facDialog.region,
          district: (facDialog.district || "").trim(), type: facDialog.type || "",
        });
        toast.success("Facility updated");
      } else {
        await api.post("/orgs/facilities/custom", {
          name: facDialog.name.trim(), region: facDialog.region,
          district: (facDialog.district || "").trim(), type: facDialog.type || "",
        });
        toast.success("Facility added");
      }
      setFacDialog(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save facility"); }
    finally { setSaving(false); }
  };

  const saveChapter = async () => {
    setSaving(true);
    try {
      const body = { name: chapDialog.name.trim(), region: chapDialog.region, district: chapDialog.district || "", facilities: chapDialog.facilities };
      if (chapDialog.id) await api.put(`/orgs/chapters/custom/${chapDialog.id}`, body);
      else await api.post("/orgs/chapters/custom", body);
      toast.success(chapDialog.id ? "Chapter updated" : "Chapter created");
      setChapDialog(null); setChapFacSearch(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save chapter"); }
    finally { setSaving(false); }
  };

  const deleteChapter = async (c) => {
    if (!window.confirm(`Delete the chapter "${c.name}"? This only removes the grouping, not any facilities or members.`)) return;
    try {
      await api.delete(`/orgs/chapters/custom/${c.id}`);
      toast.success("Chapter deleted"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const toggleChapFac = (name) => setChapDialog((d) => ({
    ...d, facilities: d.facilities.includes(name) ? d.facilities.filter((f) => f !== name) : [...d.facilities, name],
  }));


  const openCoverage = async (region) => {
    setCoverage({ region, facilities: null });
    const { data } = await api.get("/managers/region-facilities", { params: { region_name: region } });
    setCoverage(data);
  };

  const exportAudit = async () => {
    setAuditExporting(true);
    try {
      const res = await api.get("/export/audit-log", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Audit log exported");
    } catch { toast.error("Export failed"); }
    finally { setAuditExporting(false); }
  };

  const load = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await api.get("/orgs/structure");
    setData(data);
    api.get("/managers").then((r) => setManagers(r.data)).catch(() => {});
    api.get("/managers/stats").then((r) => setMstats(r.data)).catch(() => {});
    api.get("/audit-log", { params: { page_size: 100 } }).then((r) => setAudit(r.data.items)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const exportManagers = async () => {
    setExporting(true);
    try {
      const res = await api.get("/export/managers", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `managers_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Managers exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const go = (params) => navigate(`/members?${new URLSearchParams(params).toString()}`);

  const deleteFacility = async (name) => {
    if (!window.confirm(`Delete "${name}" from the organization? This only works if no members are attached.`)) return;
    try {
      await api.post("/orgs/facilities/delete-by-name", { name });
      toast.success("Facility deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const facilities = data.facilities.filter((f) =>
    !facSearch || `${f.name} ${f.region} ${f.district}`.toLowerCase().includes(facSearch.toLowerCase()));

  return (
    <Layout>
      <div className="animate-fade-up">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Administration</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Organization</h1>
        <p className="text-sm text-muted-foreground mt-2">{isAdmin ? "Live structure from member records. Click any region, chapter or facility to open the Members directory filtered to it." : "Assign management roles to existing members within your purview."}</p>

        <Tabs value={section} onValueChange={setSection} className="mt-6">
          <TabsList className="max-w-full h-auto flex-wrap justify-start">
            {isAdmin && <>
            <TabsTrigger value="regions" data-testid="tab-regions"><MapPin size={16} className="mr-1.5" /> Regions</TabsTrigger>
            <TabsTrigger value="chapters" data-testid="tab-chapters"><UsersThree size={16} className="mr-1.5" /> Chapters</TabsTrigger>
            <TabsTrigger value="facilities" data-testid="tab-facilities"><Buildings size={16} className="mr-1.5" /> Facilities</TabsTrigger>
            </>}
            <TabsTrigger value="staff" data-testid="tab-staff"><IdentificationBadge size={16} className="mr-1.5" /> Staff &amp; Roles</TabsTrigger>
            {isAdmin && <>
            <TabsTrigger value="managers" data-testid="tab-managers"><IdentificationBadge size={16} className="mr-1.5" /> Managers</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates"><Files size={16} className="mr-1.5" /> Templates</TabsTrigger>
            {isSuper && <TabsTrigger value="forms" data-testid="tab-forms"><ListChecks size={16} className="mr-1.5" /> Form Builder</TabsTrigger>}
            <TabsTrigger value="audit" data-testid="tab-audit"><ClockCounterClockwise size={16} className="mr-1.5" /> Audit Log</TabsTrigger>
            <TabsTrigger value="footer" data-testid="tab-footer"><Globe size={16} className="mr-1.5" /> Website Footer</TabsTrigger>
            </>}
          </TabsList>

          <TabsContent value="staff" className="mt-5"><Staff embedded /></TabsContent>
          {isAdmin && <TabsContent value="templates" className="mt-5"><Templates embedded /></TabsContent>}
          {isSuper && <TabsContent value="forms" className="mt-5"><FormBuilder embedded /></TabsContent>}
          {isAdmin && <TabsContent value="footer" className="mt-5"><FooterSettings /></TabsContent>}

          {/* Regions */}
          <TabsContent value="regions" className="mt-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.regions.map((r) => (
                <button key={r.name} data-testid={`region-${r.name}`} onClick={() => go({ region: r.name })}
                  className="text-left group">
                  <Card className="p-4 flex items-center justify-between hover:border-primary/50 hover:bg-primary/5 transition-colors">
                    <div className="flex items-center gap-2">
                      <MapPin size={18} className="text-primary" />
                      <span className="font-medium">{r.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Count n={r.count} />
                      <CaretRight size={16} className="text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Card>
                </button>
              ))}
              {data.regions.length === 0 && <p className="text-sm text-muted-foreground">No regions found.</p>}
            </div>
          </TabsContent>

          {/* Chapters */}
          <TabsContent value="chapters" className="mt-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <p className="text-sm text-muted-foreground">Teaching-hospital chapters are grouped by facility code prefix. Admin chapters are custom groups of facilities.</p>
              {isAdmin && (
                <Button size="sm" className="gap-1.5" data-testid="add-chapter-button"
                  onClick={() => { setChapDialog({ name: "", region: regionNames[0] || "", district: "", facilities: [] }); setChapFacSearch(""); }}>
                  <Plus size={16} weight="bold" /> Add chapter
                </Button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.chapters.map((c) => (
                <Card key={c.id || c.code} data-testid={`chapter-${c.id || c.code}`}
                  className="p-4 flex items-center justify-between hover:border-primary/50 transition-colors group">
                  <button type="button" className="text-left min-w-0 flex-1"
                    onClick={() => c.type === "custom" ? setChapDialog({ id: c.id, name: c.name, region: c.region, district: c.district || "", facilities: c.facilities || [] }) : go({ prefix: c.prefix, chapter: c.code })}>
                    <p className="font-medium truncate">{c.code}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.type === "custom" ? `${c.region}${c.district ? " · " + c.district : ""} · ${(c.facilities || []).length} facilities` : `${c.name} · ${c.prefix}`}</p>
                  </button>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <Count n={c.count} />
                    {isAdmin && c.type === "custom" && (
                      <>
                        <button type="button" data-testid={`edit-chapter-${c.id}`} title="Edit chapter"
                          onClick={() => setChapDialog({ id: c.id, name: c.name, region: c.region, district: c.district || "", facilities: c.facilities || [] })}
                          className="text-muted-foreground hover:text-primary transition-colors"><PencilSimple size={16} weight="bold" /></button>
                        <button type="button" data-testid={`delete-chapter-${c.id}`} title="Delete chapter"
                          onClick={() => deleteChapter(c)}
                          className="text-muted-foreground hover:text-destructive transition-colors"><Trash size={16} weight="bold" /></button>
                      </>
                    )}
                    {c.type !== "custom" && <CaretRight size={16} className="text-muted-foreground" />}
                  </div>
                </Card>
              ))}
              {data.chapters.length === 0 && <p className="text-sm text-muted-foreground">No chapters in your scope.</p>}
            </div>
          </TabsContent>

          {/* Facilities */}
          <TabsContent value="facilities" className="mt-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="relative max-w-md flex-1 min-w-[200px]">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="facility-search" value={facSearch} onChange={(e) => setFacSearch(e.target.value)}
                  placeholder="Search facilities…" className="pl-10" />
              </div>
              {isAdmin && (
                <Button size="sm" className="gap-1.5" data-testid="add-facility-button"
                  onClick={() => setFacDialog({ name: "", region: regionNames[0] || "", district: "", type: "" })}>
                  <Plus size={16} weight="bold" /> Add facility
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">{facilities.length.toLocaleString()} facilities</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {facilities.map((f, i) => (
                <Card key={`${f.name}-${f.region}-${f.district}-${i}`} data-testid={`facility-${f.name}`}
                  className="p-4 flex items-start justify-between hover:border-primary/50 hover:bg-primary/5 transition-colors group">
                  <button type="button" onClick={() => go({ region: f.region, district: f.district, facility: f.name })}
                    className="text-left min-w-0 flex-1">
                    <p className="font-medium truncate">{f.name}{f.custom && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-primary">new</span>}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{[f.district, f.region].filter(Boolean).join(" · ") || "—"}</p>
                  </button>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <Count n={f.count} />
                    {isAdmin && (
                      <button type="button" data-testid={`edit-facility-${f.name}`}
                        onClick={(e) => { e.stopPropagation(); setFacDialog({ original: f.name, name: f.name, region: f.region || regionNames[0] || "", district: f.district || "", type: f.type || "" }); }}
                        className="text-muted-foreground hover:text-primary transition-colors" title="Edit facility">
                        <PencilSimple size={16} weight="bold" />
                      </button>
                    )}
                    {isAdmin && (
                      <button type="button" data-testid={`delete-facility-${f.name}`}
                        onClick={(e) => { e.stopPropagation(); deleteFacility(f.name); }}
                        className="text-muted-foreground hover:text-destructive transition-colors" title="Delete facility">
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                    <CaretRight size={16} className="text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Card>
              ))}
              {facilities.length === 0 && <p className="text-sm text-muted-foreground">No facilities found.</p>}
            </div>
          </TabsContent>

          {/* Managers */}
          <TabsContent value="managers" className="mt-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              {mstats && (
                <div className="flex gap-2 flex-wrap">
                  {[["National", mstats.totals.national], ["Regional", mstats.totals.regional], ["Chapter", mstats.totals.chapter], ["Facility", mstats.totals.facility]].map(([l, n]) => (
                    <div key={l} className="rounded-lg border border-border px-4 py-2" data-testid={`mstat-${l}`}>
                      <p className="text-xs text-muted-foreground">{l}</p>
                      <p className="font-display text-xl font-bold tabular-nums">{n}</p>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={exportManagers} disabled={exporting} variant="outline"
                data-testid="export-managers-button" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <DownloadSimple size={18} weight="bold" /> {exporting ? "Exporting…" : "Export to Excel"}
              </Button>
            </div>

            {/* per-region coverage chart */}
            {mstats && (
              <Card className="p-4 mb-4" data-testid="manager-chart">
                <p className="text-sm font-medium mb-1">Facility manager coverage per region</p>
                <p className="text-xs text-muted-foreground mb-3">Green = facilities with a manager · grey = still needed</p>
                <div className="space-y-2">
                  {mstats.per_region.filter((r) => r.facility_total > 0).map((r) => {
                    const pct = r.facility_total ? Math.round((r.facility / r.facility_total) * 100) : 0;
                    return (
                      <div key={r.region} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5" data-testid={`coverage-${r.region}`} onClick={() => openCoverage(r.region)}>
                        <span className="w-32 shrink-0 truncate text-muted-foreground flex items-center gap-1">
                          <span className={`inline-block h-2 w-2 rounded-full ${r.has_regional ? "bg-emerald-500" : "bg-amber-400"}`} title={r.has_regional ? "Has regional manager" : "No regional manager"} />
                          {r.region}
                        </span>
                        <div className="flex-1 h-4 rounded bg-muted overflow-hidden flex">
                          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-28 shrink-0 text-muted-foreground tabular-nums">{r.facility}/{r.facility_total} · {r.chapter}C</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            <Card className="border-border overflow-hidden">
              <div className="overflow-auto max-h-[calc(100vh-460px)]">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Assignment</TableHead>
                      <TableHead className="hidden md:table-cell">Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {managers.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No managers assigned yet.</TableCell></TableRow>
                    ) : managers.map((m) => (
                      <TableRow key={m.id} data-testid={`org-manager-${m.staff_id}`}>
                        <TableCell><p className="font-medium">{m.name}</p><p className="text-xs text-muted-foreground font-mono">{m.staff_id}</p></TableCell>
                        <TableCell><span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary/10 text-primary">{ROLE_LABELS_MAP[m.role]}</span></TableCell>
                        <TableCell className="text-sm">{m.scope}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{m.email || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* Audit log */}
          <TabsContent value="audit" className="mt-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <p className="text-sm text-muted-foreground">Record of approvals, rejections, archives, restores and role changes.</p>
              <Button onClick={exportAudit} disabled={auditExporting} variant="outline" data-testid="export-audit-button"
                className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <DownloadSimple size={18} weight="bold" /> {auditExporting ? "Exporting…" : "Export to Excel"}
              </Button>
            </div>
            <Card className="border-border overflow-hidden">
              <div className="overflow-auto max-h-[calc(100vh-360px)]">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
                    <TableRow className="hover:bg-transparent">
                      <TableHead>When</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead className="hidden md:table-cell">By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audit.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No activity recorded yet.</TableCell></TableRow>
                    ) : audit.map((a) => (
                      <TableRow key={a.id} data-testid={`audit-${a.id}`}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{a.at ? new Date(a.at).toLocaleString() : "—"}</TableCell>
                        <TableCell><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted">{a.action.replace(/_/g, " ")}</span></TableCell>
                        <TableCell className="font-mono text-xs">{a.target}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[18rem] truncate">{a.details}</TableCell>
                        <TableCell className="hidden md:table-cell text-xs font-mono text-muted-foreground">{a.actor_staff_id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add facility dialog */}
      <Dialog open={!!facDialog} onOpenChange={(o) => !o && setFacDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="add-facility-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{facDialog?.original ? "Edit facility" : "Add facility"}</DialogTitle>
            <DialogDescription>{facDialog?.original ? "Changes sync to all members attached to this facility." : "It becomes selectable everywhere members are assigned, even before anyone joins it."}</DialogDescription>
          </DialogHeader>
          {facDialog && (
            <div className="space-y-3 py-1">
              <div className="space-y-1.5">
                <Label>Facility name</Label>
                <Input value={facDialog.name} data-testid="facility-name-input"
                  onChange={(e) => setFacDialog((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. St. Luke Clinic" />
              </div>
              <div className="space-y-1.5">
                <Label>Region</Label>
                <Select value={facDialog.region} onValueChange={(v) => setFacDialog((d) => ({ ...d, region: v }))}>
                  <SelectTrigger data-testid="facility-region-select"><SelectValue placeholder="Select region" /></SelectTrigger>
                  <SelectContent>{regionNames.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Type of facility</Label>
                <Select value={facDialog.type} onValueChange={(v) => setFacDialog((d) => ({ ...d, type: v }))}>
                  <SelectTrigger data-testid="facility-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {["Private", "MOH", "GHS", "CHAG", "Academia/Research"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>District</Label>
                <SearchableSelect value={facDialog.district} onChange={(v) => setFacDialog((d) => ({ ...d, district: v }))}
                  options={districtOpts} placeholder={facDialog.region ? "Search districts…" : "Select a region first"}
                  allLabel="" testId="facility-district-select" className="w-full" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFacDialog(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveFacility} disabled={saving || !facDialog?.name?.trim() || !facDialog?.region} data-testid="save-facility-button">
              {saving ? "Saving…" : facDialog?.original ? "Save changes" : "Add facility"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit chapter dialog */}
      <Dialog open={!!chapDialog} onOpenChange={(o) => { if (!o) { setChapDialog(null); setChapFacSearch(""); } }}>
        <DialogContent className="max-w-md" data-testid="chapter-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{chapDialog?.id ? "Edit chapter" : "Add chapter"}</DialogTitle>
            <DialogDescription>Name the chapter, pick its region, then choose which facilities belong to it.</DialogDescription>
          </DialogHeader>
          {chapDialog && (
            <div className="space-y-3 py-1">
              <div className="space-y-1.5">
                <Label>Chapter name</Label>
                <Input value={chapDialog.name} data-testid="chapter-name-input"
                  onChange={(e) => setChapDialog((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Kumasi Metro Chapter" />
              </div>
              <div className="space-y-1.5">
                <Label>Region</Label>
                <Select value={chapDialog.region} onValueChange={(v) => setChapDialog((d) => ({ ...d, region: v }))}>
                  <SelectTrigger data-testid="chapter-region-select"><SelectValue placeholder="Select region" /></SelectTrigger>
                  <SelectContent>{regionNames.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>District <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <SearchableSelect value={chapDialog.district} onChange={(v) => setChapDialog((d) => ({ ...d, district: v }))}
                  options={chapDistrictOpts} placeholder={chapDialog.region ? "Search districts…" : "Select a region first"}
                  allLabel="" testId="chapter-district-select" className="w-full" />
              </div>
              <div className="space-y-1.5">
                <Label>Facilities ({chapDialog.facilities.length} selected)</Label>
                <Input value={chapFacSearch} onChange={(e) => setChapFacSearch(e.target.value)}
                  placeholder="Search facilities in this region…" data-testid="chapter-facility-search" />
                <div className="max-h-56 overflow-auto rounded-md border border-border divide-y divide-border">
                  {data.facilities.filter((f) => f.region === chapDialog.region &&
                    (!chapFacSearch || f.name.toLowerCase().includes(chapFacSearch.toLowerCase())))
                    .slice(0, 200)
                    .map((f) => (
                      <label key={f.name} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                        data-testid={`chapter-fac-option-${f.name}`}>
                        <Checkbox checked={chapDialog.facilities.includes(f.name)} onCheckedChange={() => toggleChapFac(f.name)} />
                        <span className="truncate">{f.name}</span>
                      </label>
                    ))}
                  {data.facilities.filter((f) => f.region === chapDialog.region).length === 0 && (
                    <p className="px-3 py-4 text-sm text-muted-foreground text-center">No facilities in this region yet.</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setChapDialog(null); setChapFacSearch(""); }} disabled={saving}>Cancel</Button>
            <Button onClick={saveChapter} disabled={saving || !chapDialog?.name?.trim() || !chapDialog?.region} data-testid="save-chapter-button">
              {saving ? "Saving…" : chapDialog?.id ? "Save changes" : "Create chapter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!coverage} onOpenChange={(o) => !o && setCoverage(null)}>
        <DialogContent className="max-w-md" data-testid="coverage-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{coverage?.region} — facilities needing a manager</DialogTitle></DialogHeader>
          {!coverage?.facilities ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <div className="max-h-[60vh] overflow-auto space-y-1">
              {coverage.facilities.filter((f) => !f.has_manager).length === 0 ? (
                <p className="text-sm text-emerald-600">All facilities have a manager 🎉</p>
              ) : coverage.facilities.filter((f) => !f.has_manager).map((f) => (
                <div key={f.facility} className="text-sm py-1.5 px-2 rounded bg-muted/50">{f.facility}</div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
